import React, { useEffect, useRef, useState } from "react";
import { Pencil, Check, X, RotateCcw } from "lucide-react";
import { useAuth } from "@/lib/auth";

/**
 * Inline click-to-edit text. Admins see a small pencil affordance on hover;
 * clicking it (or Enter/F2 while focused) turns the node into an editable
 * input. Non-admin viewers just see the resolved text.
 *
 * The parent must pass:
 *   - `path`           unique dotted key persisted on the backend
 *   - `defaultValue`   raw source value (from PlayHQ). Shown when no override
 *                      exists yet.
 *   - `override`       current override value or undefined
 *   - `onSave(value)`  called with the new string (admin action)
 *   - `onClear()`      optional; called to revert to the source value
 *
 * The wrapper element defaults to `<span>` for inline text but can be swapped
 * (`as="div"`) when block-level editing (e.g. paragraphs) is needed.
 */
export default function EditableText({
  path,
  defaultValue = "",
  override,
  onSave,
  onClear,
  as: Tag = "span",
  className = "",
  multiline = false,
  placeholder = "",
  emptyLabel = "—",
  ...rest
}) {
  const { isAdmin } = useAuth();
  const [editing, setEditing] = useState(false);
  const hasOverride = override !== undefined && override !== null;
  const displayed = hasOverride ? override : defaultValue;
  const [draft, setDraft] = useState(String(displayed ?? ""));
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) {
      // Start empty so admins can type fresh text without having to first
      // clear the pre-filled value. Cancelling restores the source value.
      setDraft("");
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [editing, displayed]);

  const commit = async () => {
    const val = draft;
    if (val === (displayed ?? "")) { setEditing(false); return; }
    try { await onSave?.(val); setEditing(false); }
    catch { /* keep editing so user can retry */ }
  };
  const cancel = () => { setEditing(false); setDraft(String(displayed ?? "")); };
  const revert = async () => { try { await onClear?.(); setEditing(false); } catch {} };

  // View mode (member / public / admin-not-editing)
  if (!editing) {
    const shown = String(displayed ?? "");
    const isEmpty = !shown;
    return (
      <Tag
        {...rest}
        className={`${className} ${isAdmin ? "relative group/edit cursor-text hover:bg-[hsl(var(--accent))]/5 hover:outline hover:outline-1 hover:outline-dashed hover:outline-[hsl(var(--accent))]/40 hover:rounded-sm transition-colors" : ""}`}
        data-editable-path={path}
        onClick={isAdmin ? (e) => { e.stopPropagation(); setEditing(true); } : undefined}
        title={isAdmin ? `Click to edit · ${path}` : undefined}
      >
        {isEmpty ? (isAdmin ? <span className="italic text-muted-foreground">{emptyLabel}</span> : emptyLabel) : shown}
        {isAdmin && (
          <Pencil className="inline-block ml-1 h-3 w-3 opacity-0 group-hover/edit:opacity-70 align-baseline text-[hsl(var(--accent))]" />
        )}
      </Tag>
    );
  }

  // Edit mode (admin only)
  const commonProps = {
    ref: inputRef,
    value: draft,
    onChange: (e) => setDraft(e.target.value),
    onKeyDown: (e) => {
      if (!multiline && e.key === "Enter") { e.preventDefault(); commit(); }
      if (e.key === "Escape") { e.preventDefault(); cancel(); }
    },
    placeholder,
    "data-testid": `editable-input-${path}`,
    className: "min-w-0 bg-white text-foreground border border-[hsl(var(--accent))] rounded px-1.5 py-0.5 text-inherit font-inherit shadow-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent))]/40",
    onClick: (e) => e.stopPropagation(),
  };

  return (
    <Tag
      {...rest}
      className={`${className} relative inline-flex items-center gap-1 align-baseline`}
    >
      {multiline ? (
        <textarea rows={3} {...commonProps} className={commonProps.className + " w-full min-w-[240px] resize-y"} />
      ) : (
        <input type="text" {...commonProps} style={{ width: `${Math.max(12, Math.min(48, draft.length + 3))}ch` }} />
      )}
      <span className="inline-flex items-center gap-0.5 ml-1">
        <button type="button" onClick={commit} title="Save" className="h-6 w-6 inline-flex items-center justify-center rounded bg-[hsl(var(--accent))] text-white hover:brightness-110" data-testid={`editable-save-${path}`}>
          <Check className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={cancel} title="Cancel" className="h-6 w-6 inline-flex items-center justify-center rounded bg-muted hover:bg-muted/70 text-muted-foreground" data-testid={`editable-cancel-${path}`}>
          <X className="h-3.5 w-3.5" />
        </button>
        {hasOverride && onClear && (
          <button type="button" onClick={revert} title="Revert to source (PlayHQ) value" className="h-6 w-6 inline-flex items-center justify-center rounded bg-muted hover:bg-red-500/10 text-muted-foreground hover:text-red-600" data-testid={`editable-revert-${path}`}>
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
    </Tag>
  );
}
